import { ChangeEvent, useMemo, useState } from "react";
import { ProductListItem } from "../../../../types/product.type";
import { numberWithCommas } from "../../../../utils";
import { COMPACT_QUERY } from "../../../../utils/hooks/useCompact";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import Skeleton from "../../../ReprintAuth/Skeleton";
import CataloguePagination from "../../shared/Pagination";
import { catalogueRowHeight } from "../../shared/constants";
import AddProductsModal from "./AddProductsModal";
import ProductLineRow, { LinePeriodActions } from "./ProductLineRow";
import { PricebookEditor } from "./usePricebookEditor";

const compact = "@media " + COMPACT_QUERY;

const LIMIT = 20;
const SEARCH_FIELD_WIDTH = 320;
/** A small input over its helper line is the tallest thing in an editable row. */
const EDITABLE_ROW_CONTENT_HEIGHT = 40;
const ROW_HEIGHT = catalogueRowHeight(EDITABLE_ROW_CONTENT_HEIGHT);

const EMPTY_MESSAGE = "ยังไม่มีสินค้าในสมุดราคานี้";
const NO_SEARCH_RESULT_MESSAGE = "ไม่พบสินค้าที่ตรงกับคำค้นหา";
const LOAD_FAILED_MESSAGE =
  "โหลดรายการสินค้าไม่สำเร็จ จึงยังแก้ไขไม่ได้ กรุณาโหลดหน้านี้ใหม่";

const COLUMNS: { label: string; width?: string }[] = [
  { label: "ลำดับสินค้า", width: "7%" },
  { label: "รหัส", width: "9%" },
  { label: "สินค้า" },
  { label: "หน่วย", width: "6%" },
  { label: "ราคาต่อหน่วย", width: "11%" },
  { label: "ส่วนลด %", width: "10%" },
  { label: "สุทธิรวม VAT", width: "9%" },
  { label: "สุทธิก่อน VAT", width: "9%" },
  { label: "เปิดขาย", width: "8%" },
];
const PERIOD_COLUMN = { label: "ช่วงราคา", width: "8%" };
const DELETE_COLUMN = { label: "ลบ", width: "5%" };

interface PricebookProductTableProps {
  editor: PricebookEditor;
  /** False for readers and for the books whose prices are kept elsewhere. */
  canEdit: boolean;
  /** The "+ ช่วงราคา" column and the chip that edits a running period; null where none is written. */
  periodActions: LinePeriodActions | null;
}

/**
 * The สินค้า tab: every line of the book, held in memory because a save replaces the whole
 * set, so the search and the paging are done here rather than by the API.
 */
export default function PricebookProductTable({
  editor,
  canEdit,
  periodActions,
}: PricebookProductTableProps) {
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(LIMIT);
  const [picking, setPicking] = useState<boolean>(false);

  const columns = [
    ...COLUMNS,
    ...(periodActions ? [PERIOD_COLUMN] : []),
    ...(canEdit ? [DELETE_COLUMN] : []),
  ];
  const { lines, loading, loadFailed, invalidCodes } = editor;

  const matchedLines = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return lines;
    return lines.filter(
      (line) =>
        line.productCode.toLowerCase().includes(term) ||
        line.productName.toLowerCase().includes(term),
    );
  }, [lines, search]);

  const addedProductCodes = useMemo(
    () => new Set(lines.map((line) => line.productCode)),
    [lines],
  );

  // Removing lines shortens the list, which can leave the page in view past the end.
  const pageCount = Math.max(1, Math.ceil(matchedLines.length / limit));
  if (page > pageCount) setPage(pageCount);

  const firstVisibleIndex = (page - 1) * limit;
  const visibleLines = matchedLines.slice(
    firstVisibleIndex,
    firstVisibleIndex + limit,
  );
  const emptyMessage = search.trim() ? NO_SEARCH_RESULT_MESSAGE : EMPTY_MESSAGE;

  const handleChangeSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const handleOpenPicker = () => setPicking(true);
  const handleClosePicker = () => setPicking(false);

  // Added lines go to the end of the book, so the table moves to its last page to show them;
  // a filter left from before would hide them.
  const handleConfirmProducts = (products: ProductListItem[]) => {
    editor.addProducts(products);
    setPicking(false);
    setSearch("");
    setPage(Math.max(1, Math.ceil((lines.length + products.length) / limit)));
  };

  return (
    <Stack gap={2}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        flexWrap="wrap"
      >
        <TextField
          size="small"
          placeholder="ค้นหาชื่อหรือรหัสสินค้า"
          value={search}
          onChange={handleChangeSearch}
          sx={{ width: SEARCH_FIELD_WIDTH, maxWidth: "100%" }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Typography variant="body2" color="text.secondary">
            {numberWithCommas(lines.length)} รายการ
          </Typography>
          {canEdit && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              disabled={loading || loadFailed}
              onClick={handleOpenPicker}
            >
              เพิ่มสินค้า
            </Button>
          )}
        </Stack>
      </Stack>

      <TableContainer>
        <Table>
          <TableHead
            sx={{ bgcolor: (theme) => theme.palette.background.default }}
          >
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.label}
                  align="center"
                  sx={{ width: column.width }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <Skeleton
                columns={columns.length}
                rows={limit}
                rowHeight={ROW_HEIGHT}
              />
            )}
            {!loading && visibleLines.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{ py: 5, [compact]: { py: 3 } }}
                >
                  <Typography color="text.secondary" fontWeight={500}>
                    {loadFailed ? LOAD_FAILED_MESSAGE : emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              visibleLines.map((line, index) => (
                <ProductLineRow
                  key={line.productCode}
                  line={line}
                  bookDiscountText={editor.bookDiscountText}
                  canEdit={canEdit}
                  isFlagged={invalidCodes.includes(line.productCode)}
                  striped={index % 2 === 1}
                  onChangeUnitPrice={editor.changeUnitPrice}
                  onChangeOwnDiscount={editor.changeOwnDiscount}
                  onToggleStatus={editor.toggleStatus}
                  onRemove={editor.removeLine}
                  periodActions={periodActions}
                />
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <CataloguePagination
        totalCount={matchedLines.length}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
      />

      <AddProductsModal
        open={picking}
        addedProductCodes={addedProductCodes}
        onClose={handleClosePicker}
        onConfirm={handleConfirmProducts}
      />
    </Stack>
  );
}
